import React, { MouseEventHandler, TouchEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableRow, Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AreaConfType, PitConfType } from 'types/machineConf/machineConfType';
import { to2DArrayArea, to2DArrayPit, toAreaConfFrom2DArray } from 'utils/machineConf.helper';
import pitMarkIcon from 'assets/images/pitMarkIcon.svg';
import COMMON from 'constants/common';
import CLSFCN from 'constants/classification';
import { useAppSelector } from 'redux/hooks';

/* ************ constant ************ */
const XY_AREA_SIZE = 390;
const PRIZE_AREA_MAX = 100;

type Props = {
  areaConf?: AreaConfType;
  pitConf?: PitConfType;
  areaValue: string;
  onChange: (areaConf: AreaConfType) => void;
  className?: string;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: '40px',
    userSelect: 'none',
    touchAction: 'none',
    '& *': {
      touchAction: 'none',
    },
  },
  container: {
    position: 'relative',
    width: '414px',
    height: '414px',
    borderWidth: '0px',
    padding: '16px 0px 0px 12px',
  },
  table: {
    border: '1px solid',
    borderColor: theme.base.tableBorder,
    borderCollapse: 'collapse',
    width: '390px',
    height: '390px',
    '& .area0': {
      backgroundColor: theme.base.area0,
    },
    '& .area1': {
      backgroundColor: theme.base.area1,
    },
    '& .area2': {
      backgroundColor: theme.base.area2,
    },
    '& .area3': {
      backgroundColor: theme.base.area3,
    },
    '& .area4': {
      backgroundColor: theme.base.area4,
    },
    '& .area5': {
      backgroundColor: theme.base.area5,
    },
    '& td, th': {
      borderCollapse: 'collapse',
      borderWidth: '1px',
      borderColor: theme.base.tableBorder,
    },
  },
  tableRow: {
    height: '65px',
  },
  tableCell: {
    position: 'relative',
    padding: '0',
    width: '65px',
    borderRightStyle: 'solid',
  },
  evenColumnCell: {
    borderRightStyle: 'dashed',
  },
  evenRowCell: {
    borderBottomStyle: 'dashed',
  },
  pitBlock: {
    width: '100%',
    height: '100%',
    padding: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pitIcon: {
    pointerEvents: 'none',
  },
  pitIconSimple: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    height: '114px',
    width: '114px',
    zIndex: 1,
  },
  pitIconDetail: {
    position: 'absolute',
    top: '4px',
    left: '4px',
    height: '57px',
    width: '57px',
  },
  pinpointPit: {
    backgroundColor: theme.palette.common.black,
    border: '2px solid',
    borderColor: theme.palette.common.white,
    borderRadius: '100%',
    position: 'absolute',
    width: '24px',
    height: '24px',
    zIndex: 1,
  },
}));

const PinpointPit: React.FC<React.PropsWithChildren<{ status: string; x: number | null; y: number | null }>> = ({ status, x, y }) => {
  // status,x,yが有効でない場合は空を返す。
  if (status !== COMMON.STATUS.NORMAL || x === null || y === null) {
    return <></>;
  }

  const { classes } = useStyles();
  const leftRight = useAppSelector((state) => state.machineConf.selectedStation.leftRight);

  const toScreenPosY = useCallback(
    (pitY: number) => XY_AREA_SIZE - Math.round((pitY * XY_AREA_SIZE) / PRIZE_AREA_MAX),
    [],
  );
  const leftToScreenPos = useCallback(
    (pitX: number, pitY: number) => ({
      x: Math.round((pitX * XY_AREA_SIZE) / PRIZE_AREA_MAX),
      y: toScreenPosY(pitY),
    }),
    [],
  );
  const rightToScreenPos = useCallback(
    (pitX: number, pitY: number) => ({
      x: XY_AREA_SIZE - Math.round((pitX * XY_AREA_SIZE) / PRIZE_AREA_MAX),
      y: toScreenPosY(pitY),
    }),
    [],
  );
  const toScreenPos = leftRight === COMMON.LEFT_RIGHT.LEFT ? leftToScreenPos : rightToScreenPos;
  const axes = toScreenPos(x || 0, y || 0);

  const style = {
    top: axes.y,
    left: axes.x,
  };
  return (
    <Box style={style} className={classes.pinpointPit}>
      <span />
    </Box>
  );
};

// TouchMove中はこのコンポーネント内部のステートを切り替えるのみとする
// TouchEnd時にonChangeを介して渡された基のステートを更新する
const AreaConfPanel: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { areaValue, areaConf, pitConf, onChange, className } = props;
  const { classes } = useStyles();
  const [areas, setAreas] = useState(to2DArrayArea(areaConf));
  const pits = useRef(to2DArrayPit(pitConf));

  useEffect(() => {
    setAreas(to2DArrayArea(areaConf));
  }, [areaConf]);

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    updateArea(event.clientX, event.clientY);
  };

  const handleTouchStartAndMove: TouchEventHandler<HTMLDivElement> = (event) => {
    // ※TouchMoveイベントは指を離すまで一番最初にタッチしたコンポーネントに対して発生し続ける
    const touch = event.changedTouches[0];
    updateArea(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    // ※PCとモバイルでイベントの発生仕方が異なる
    //   PC：マウスダウンした要素に関係なく、単純にマウスアップした要素でイベントが発生する→MouseUpだけでは編集終了を捕捉できないのでMouseLeaveも追加
    //   モバイル：要素内でタッチ開始すれば要素外でタッチ終了してもタッチ開始した要素でTouchEndイベントが発生する
    if (areaConf) {
      const newAreaConf = toAreaConfFrom2DArray(areaConf, areas);
      onChange(newAreaConf);
    }
  };

  const updateArea = (clientX: number, clientY: number) => {
    // タッチしているエリアを取得
    const element = document.elementFromPoint(clientX, clientY) as HTMLTableCellElement;
    if (element) {
      // 最初にタッチした落とし穴の値を反転した値を入力モードとして設定

      // 現在の入力モードとタッチしている落とし穴の値が違ったら落とし穴の値を変更
      const x = Number(element.dataset.x);
      const y = Number(element.dataset.y);
      if (!element.dataset.x || !element.dataset.y) return;
      if (areaValue !== areas[y][x]) {
        setAreas((prevAreas) => {
          const newAreas = [...prevAreas];
          newAreas[y][x] = areaValue;
          return newAreas;
        });
      }
    }
  };

  const displayPit = useCallback((i: number, j: number) => {
    if (pitConf?.settingPattern === CLSFCN.PIT_PATTERN.SIMPLE) {
      // かんたんの場合は偶数時のみ処理する。
      if (j % 2 === 0 && i % 2 === 0 && pits.current[j / 2][i / 2] === COMMON.FLAG.ON)
        return (
          <Box className={`${classes.pitIcon} ${classes.pitIconSimple}`}>
            <img src={pitMarkIcon} alt="○" />
          </Box>
        );
      return <></>;
    }
    if (pits.current[j][i] === COMMON.FLAG.ON)
      return (
        <Box className={`${classes.pitIcon} ${classes.pitIconDetail}`}>
          <img src={pitMarkIcon} alt="○" />
        </Box>
      );
    return <></>;
  }, []);

  const cellStyle = useCallback(
    (x: number, y: number) =>
      `${classes.tableCell} ${(x % 2 === 0 && classes.evenColumnCell) || ''} ${
        (y % 2 === 0 && classes.evenRowCell) || ''
      }`,
    [],
  );

  // ※Boxの型定義からrefが漏れておりdivを使用
  return (
    <Box
      className={`${classes.root} ${className}`}
      onMouseDown={handleClick}
      onMouseLeave={handleTouchEnd}
      onMouseUp={handleTouchEnd}
      onTouchStart={handleTouchStartAndMove}
      onTouchMove={handleTouchStartAndMove}
      onTouchEnd={handleTouchEnd}>
      <TableContainer className={classes.container}>
        <Table className={classes.table}>
          <TableBody>
            {areas.map((row, i) => (
              <TableRow className={classes.tableRow} key={String(i)}>
                {row.map((value, j) => (
                  <TableCell data-x={j} data-y={i} className={`${cellStyle(j, i)} area${value}`} key={String(j)}>
                    {pitConf?.settingPattern !== CLSFCN.PIT_PATTERN.PINPOINT && displayPit(j, i)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pitConf?.settingPattern === CLSFCN.PIT_PATTERN.PINPOINT && (
          <>
            <PinpointPit status={pitConf.pit1Status} x={pitConf.pit1x} y={pitConf.pit1y} />
            <PinpointPit status={pitConf.pit2Status} x={pitConf.pit2x} y={pitConf.pit2y} />
            <PinpointPit status={pitConf.pit3Status} x={pitConf.pit3x} y={pitConf.pit3y} />
            <PinpointPit status={pitConf.pit4Status} x={pitConf.pit4x} y={pitConf.pit4y} />
          </>
        )}
      </TableContainer>
    </Box>
  );
};

export default AreaConfPanel;
